import React, { useState, useRef, useEffect } from "react";
import { Calendar as CalendarIcon, X } from "lucide-react";
import searchStore from "../store/searchStore";

const MONTHS_RU = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];
const DAYS_RU = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

function formatDate(date) {
  if (!date) return "";
  return date
    .toLocaleDateString("ru-RU", { day: "numeric", month: "short" })
    .replace(".", "");
}

function isSameDay(a, b) {
  return (
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isBetween(date, start, end) {
  if (!start || !end) return false;
  const d = date.getTime();
  return d > start.getTime() && d < end.getTime();
}

function getCalendarDays(year, month) {
  const first = new Date(year, month, 1);
  let startDow = first.getDay();
  startDow = startDow === 0 ? 6 : startDow - 1;
  const days = [];
  for (let i = 0; i < startDow; i++) {
    days.push({
      date: new Date(year, month, 1 - (startDow - i)),
      currentMonth: false,
    });
  }
  const lastDay = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= lastDay; d++) {
    days.push({ date: new Date(year, month, d), currentMonth: true });
  }
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    days.push({ date: new Date(year, month + 1, i), currentMonth: false });
  }
  return days;
}

function getMonthList(today, count = 18) {
  const list = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
    list.push({ year: d.getFullYear(), month: d.getMonth() });
  }
  return list;
}

export default function DateRangePicker({
  checkIn,
  checkOut,
  onChange,
  accentColor = "#14b8a6",
  triggerVariant = "default",
  onOpen,
  onClose,
  closeSignal,
}) {
  const [open, setOpen] = useState(false);
  const [popupPos, setPopupPos] = useState(null);

  useEffect(() => {
    if (closeSignal) {
      setOpen(false);
      onClose?.();
    }
  }, [closeSignal]);
  const [hovered, setHovered] = useState(null);
  const [activeMonthIdx, setActiveMonthIdx] = useState(0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const monthList = getMonthList(today, 12);
  const calendarScrollRef = useRef(null);
  const monthRefs = useRef([]);
  const ref = useRef(null);

  const isMobile = () => window.innerWidth < 640;

  const openPopup = () => {
    if (isMobile() && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPopupPos({ top: rect.bottom + 4, left: 8, right: 8, fixed: true });
    } else {
      setPopupPos(null);
    }
  };

  const popupRef = useRef(null);

  useEffect(() => {
    function handleOutside(e) {
      const inTrigger = ref.current && ref.current.contains(e.target);
      const inPopup = popupRef.current && popupRef.current.contains(e.target);
      if (!inTrigger && !inPopup) {
        setOpen(false);
        onClose?.();
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const container = calendarScrollRef.current;
    if (!container) return;
    const handleScroll = () => {
      let closest = 0;
      let minDist = Infinity;
      monthRefs.current.forEach((el, i) => {
        if (!el) return;
        const dist = Math.abs(el.offsetTop - container.scrollTop);
        if (dist < minDist) {
          minDist = dist;
          closest = i;
        }
      });
      setActiveMonthIdx(closest);
    };
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [open]);

  const parseLocalDate = (str) => {
    if (!str) return null;
    const [y, m, d] = str.split("-").map(Number);
    return new Date(y, m - 1, d);
  };

  const formatLocalDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const startDate = parseLocalDate(checkIn);
  const endDate = parseLocalDate(checkOut);

  const handleDayClick = (date) => {
    if (date < today) return;
    if (!startDate || (startDate && endDate)) {
      onChange({ checkIn: formatLocalDate(date), checkOut: "" });
    } else {
      if (date <= startDate) {
        onChange({ checkIn: formatLocalDate(date), checkOut: "" });
      } else {
        onChange({ checkIn: checkIn, checkOut: formatLocalDate(date) });
        setOpen(false);
        onClose?.();
      }
    }
  };

  const goToMonthIdx = (idx) => {
    setActiveMonthIdx(idx);
    const el = monthRefs.current[idx];
    if (el && calendarScrollRef.current) {
      calendarScrollRef.current.scrollTo({
        top: el.offsetTop,
        behavior: "smooth",
      });
    }
  };

  const displayText =
    startDate && endDate
      ? `${formatDate(startDate)} — ${formatDate(endDate)}`
      : startDate
        ? `${formatDate(startDate)} — ...`
        : "";

  const { year: viewYear, month: viewMonth } = monthList[activeMonthIdx];
  const days = getCalendarDays(viewYear, viewMonth);
  const effectiveEnd = endDate || hovered;

  const isPill = triggerVariant === "pill";
  const isPillLight = triggerVariant === "pill-light";

  return (
    <div className="relative" ref={ref}>
      {isPill || isPillLight ? (
        <button
          onMouseDown={(e) => {
            e.stopPropagation();
            if (!open && onOpen) onOpen();
            openPopup();
            setOpen((o) => !o);
          }}
          className={`flex items-center gap-1.5 rounded-full transition-colors cursor-pointer select-none ${isPillLight ? "text-white/90 hover:bg-white/10 px-3 py-1" : "hover:bg-gray-100 px-4 py-2"}`}
        >
          <span
            className={`whitespace-nowrap ${isPillLight ? "text-white/90 text-xs" : "text-gray-700 text-sm"}`}
          >
            {displayText || "Выберите даты"}
          </span>
          {(startDate || endDate) && (
            <span
              className={
                isPillLight
                  ? "text-white/50 hover:text-white"
                  : "text-gray-400 hover:text-gray-600"
              }
              onClick={(e) => {
                e.stopPropagation();
                onChange({ checkIn: "", checkOut: "" });
                searchStore.clearCheckDates();
              }}
            >
              <X className="w-3 h-3" />
            </span>
          )}
        </button>
      ) : (
        <div
          onMouseDown={(e) => {
            e.stopPropagation();
            if (!open && onOpen) onOpen();
            openPopup();
            setOpen((o) => !o);
          }}
          className="w-full flex items-center gap-2 h-10 px-3 rounded-md bg-white text-sm text-left cursor-pointer select-none overflow-hidden"
          style={{ border: `1px solid ${accentColor}88` }}
        >
          <CalendarIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span
            className={`truncate whitespace-nowrap ${displayText ? "text-gray-900" : "text-gray-400"}`}
          >
            {displayText || "Выберите даты"}
          </span>
          {(startDate || endDate) && (
            <span
              className="ml-auto text-gray-400 hover:text-gray-600"
              onClick={(e) => {
                e.stopPropagation();
                onChange({ checkIn: "", checkOut: "" });
              }}
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
        </div>
      )}

      {open && (
        <>
          {/* Mobile backdrop */}
          <div
            className="fixed inset-0 z-40 sm:hidden"
            onMouseDown={() => {
              setOpen(false);
              onClose?.();
            }}
          />
          <div
            ref={popupRef}
            className="z-[60] bg-white rounded-2xl flex w-auto"
            style={
              popupPos
                ? {
                    position: "fixed",
                    top: popupPos.top,
                    left: popupPos.left,
                    right: popupPos.right,
                    border: "1px solid #e5e7eb",
                    boxShadow:
                      "0 10px 25px -5px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
                  }
                : {
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    marginTop: "4px",
                    border: "1px solid #e5e7eb",
                    boxShadow:
                      "0 10px 25px -5px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
                  }
            }
          >
            {/* Month sidebar */}
            <div className="block w-20 sm:w-24 flex-shrink-0 border-r border-gray-100 py-3 rounded-l-2xl overflow-hidden">
              {monthList.map(({ year, month }, idx) => {
                const isActive = idx === activeMonthIdx;
                const showYear = idx > 0 && year !== monthList[idx - 1].year;
                return (
                  <div key={`${year}-${month}`}>
                    {showYear && (
                      <div className="text-center text-xs text-gray-400 font-medium py-1 px-2">
                        {year}
                      </div>
                    )}
                    <button
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        goToMonthIdx(idx);
                      }}
                      className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${
                        isActive
                          ? "font-bold text-gray-900 bg-gray-100"
                          : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                      }`}
                    >
                      {MONTHS_RU[month]}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Calendar area */}
            <div className="flex-1 flex flex-col min-w-0 w-64 sm:w-72">
              {/* Scrollable months — no scrollbar */}
              <div
                ref={calendarScrollRef}
                className="overflow-y-auto px-3 pt-3"
                style={{
                  height: 345,
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
              >
                <style>{`div::-webkit-scrollbar { display: none; }`}</style>
                {monthList.map(({ year, month }, idx) => {
                  const mDays = getCalendarDays(year, month);
                  return (
                    <div
                      key={`${year}-${month}`}
                      ref={(el) => (monthRefs.current[idx] = el)}
                      className="mb-6"
                    >
                      <div className="text-sm font-semibold text-gray-900 mb-2 text-center">
                        {MONTHS_RU[month]} {year}
                      </div>
                      {/* Day names */}
                      <div className="grid grid-cols-7 mb-1">
                        {DAYS_RU.map((d) => (
                          <div
                            key={d}
                            className="text-center text-xs text-gray-400 py-1 font-medium"
                          >
                            {d}
                          </div>
                        ))}
                      </div>
                      {/* Days */}
                      <div className="grid grid-cols-7">
                        {mDays.map(({ date, currentMonth }, didx) => {
                          const isPast = date < today;
                          const isStart = isSameDay(date, startDate);
                          const isEnd = isSameDay(date, endDate);
                          const isInRange =
                            isBetween(date, startDate, effectiveEnd) &&
                            startDate &&
                            effectiveEnd &&
                            effectiveEnd > startDate;
                          const isToday = isSameDay(date, today);

                          return (
                            <div
                              key={didx}
                              className="relative"
                              onMouseEnter={() => {
                                if (startDate && !endDate) setHovered(date);
                              }}
                              onMouseLeave={() => setHovered(null)}
                            >
                              {isInRange && (
                                <div
                                  className="absolute inset-0"
                                  style={{
                                    backgroundColor: `${accentColor}18`,
                                  }}
                                />
                              )}
                              {isStart && (
                                <>
                                  <div
                                    className="absolute inset-0"
                                    style={{
                                      left: "50%",
                                      backgroundColor: `${accentColor}18`,
                                    }}
                                  />
                                  <div
                                    className="absolute inset-0 rounded-full mx-0.5"
                                    style={{ backgroundColor: accentColor }}
                                  />
                                </>
                              )}
                              {isEnd && (
                                <>
                                  <div
                                    className="absolute inset-0"
                                    style={{
                                      right: "50%",
                                      backgroundColor: `${accentColor}18`,
                                    }}
                                  />
                                  <div
                                    className="absolute inset-0 rounded-full mx-0.5"
                                    style={{ backgroundColor: accentColor }}
                                  />
                                </>
                              )}
                              {isStart && !endDate && !hovered && (
                                <div
                                  className="absolute inset-0 rounded-full mx-0.5"
                                  style={{ backgroundColor: accentColor }}
                                />
                              )}
                              <div
                                onClick={() => !isPast && handleDayClick(date)}
                                className={`relative flex items-center justify-center h-7 sm:h-9 text-xs select-none transition-colors z-10
                                ${
                                  isPast
                                    ? "text-gray-300 cursor-not-allowed"
                                    : isStart || isEnd
                                      ? "text-white font-semibold cursor-pointer"
                                      : isInRange
                                        ? "cursor-pointer"
                                        : currentMonth
                                          ? "text-gray-700 hover:bg-gray-100 rounded-lg cursor-pointer"
                                          : "text-gray-300 cursor-pointer"
                                }
                              `}
                              >
                                {isToday && !isStart && !isEnd ? (
                                  <span
                                    className="relative z-10 flex items-center justify-center w-7 h-7 rounded-full border-2 font-semibold"
                                    style={{
                                      borderColor: accentColor,
                                      color: accentColor,
                                    }}
                                  >
                                    {date.getDate()}
                                  </span>
                                ) : (
                                  <span className="relative z-10">
                                    {date.getDate()}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100">
                <button
                  className="text-xs text-gray-400 hover:text-gray-600 font-medium"
                  onClick={() => onChange({ checkIn: "", checkOut: "" })}
                >
                  Очистить
                </button>
                <span className="text-xs text-gray-400">
                  {!startDate
                    ? "Выберите дату заезда"
                    : !endDate
                      ? "Выберите дату выезда"
                      : ""}
                </span>
                <span />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
